import { useState, useCallback, useEffect, useMemo } from "react";
import type {
  PublicCommentConfig,
  PublicComment,
  CommentPosition
} from "../types";
import { validateFiles, buildCommentAttachments } from "../attachments";
import { fieldIdPrefix } from "../utils";

type SortMode = "top" | "new";

interface ComposeDraft {
  text: string;
  position: CommentPosition; // null = neutral
}

function antiForgeryToken(config: PublicCommentConfig): string {
  return (window as any).ajaxAntiForgeryToken || config.antiForgeryToken || "";
}

// --- session memory ---------------------------------------------------------
// Remember what the resident entered (personal info + per-item drafts) for this meeting
// during the browser session, so switching the Register/Comments/Agenda tabs (each a full
// page load) or reloading doesn't wipe the form. sessionStorage is per-tab and cleared when
// the tab closes — scoped per meeting so different meetings don't collide.
const SESSION_PREFIX = "pi-agenda-comment";
function sessionKey(offlineEventId: number, slot: string): string {
  return `${SESSION_PREFIX}:${slot}:${offlineEventId}`;
}
function loadSession<T>(key: string, fallback: T): T {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback; // sessionStorage blocked (private mode) or unparseable — degrade gracefully
  }
}
function saveSession(key: string, value: unknown): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* sessionStorage unavailable/over quota — non-fatal, just don't remember */
  }
}

async function postForm(
  url: string,
  data: Record<string, string | number | boolean>,
  config: PublicCommentConfig
): Promise<Response> {
  const params = new URLSearchParams();
  Object.entries(data).forEach(([k, v]) => {
    if (v !== null && v !== undefined) params.append(k, String(v));
  });
  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      "X-Requested-With": "XMLHttpRequest",
      __RequestVerificationToken: antiForgeryToken(config)
    },
    body: params.toString()
  });
}

export interface UseAgendaCommentsReturn {
  // personal info
  formData: Record<string, string>;
  errors: Record<string, string>;
  handleFieldChange: (fieldName: string, value: string) => void;
  commenterName: string; // derived display name for attribution
  // data
  loading: boolean;
  commentsByItem: Record<number, PublicComment[]>;
  // row/expansion state
  openItems: number[];
  toggleItem: (id: number) => void;
  searchKeyword: string;
  setSearchKeyword: (k: string) => void;
  sortFor: (itemId: number) => SortMode;
  setSort: (itemId: number, mode: SortMode) => void;
  // compose
  composeFor: (itemId: number) => ComposeDraft;
  setComposeText: (itemId: number, text: string) => void;
  setComposePosition: (itemId: number, pos: CommentPosition) => void;
  submittingItem: number | null;
  modNoticeItems: number[];
  // admin "post on behalf of the agency" toggle (top-level composer)
  canPostAsAdmin: boolean;
  postAsAdmin: boolean;
  setPostAsAdmin: (value: boolean) => void;
  orgLabel: string;
  // attachments (per item)
  attachmentsFor: (itemId: number) => File[];
  addAttachments: (itemId: number, files: FileList | File[]) => void;
  removeAttachment: (itemId: number, index: number) => void;
  attachmentErrorFor: (itemId: number) => string | undefined;
  // private (hidden) comment toggle (per item)
  keepPrivateFor: (itemId: number) => boolean;
  setKeepPrivate: (itemId: number, value: boolean) => void;
  submitComment: (itemId: number) => Promise<void>;
  // engagement
  toggleVote: (itemId: number, commentId: number) => Promise<void>;
  flagComment: (commentId: number) => Promise<void>;
  reportedFor: (commentId: number) => boolean;
  replyOpenFor: (commentId: number) => boolean;
  openReply: (commentId: number) => void;
  cancelReply: (commentId: number) => void;
  replyDraftFor: (commentId: number) => string;
  setReplyDraft: (commentId: number, text: string) => void;
  // per-reply admin "post on behalf of the agency" toggle
  replyAsAdminFor: (commentId: number) => boolean;
  setReplyAsAdmin: (commentId: number, value: boolean) => void;
  submittingReplyId: number | null;
  sendReply: (itemId: number, parentId: number) => Promise<void>;
}

export function useAgendaComments(
  config: PublicCommentConfig
): UseAgendaCommentsReturn {
  const [formData, setFormData] = useState<Record<string, string>>(() =>
    loadSession<Record<string, string>>(
      sessionKey(config.offlineEventId, "fields"),
      {}
    )
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [commentsByItem, setCommentsByItem] = useState<
    Record<number, PublicComment[]>
  >({});
  const [loading, setLoading] = useState(true);
  const [openItems, setOpenItems] = useState<number[]>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [sortByItem, setSortByItem] = useState<Record<number, SortMode>>({});
  const [composeByItem, setComposeByItem] = useState<
    Record<number, ComposeDraft>
  >(() =>
    loadSession<Record<number, ComposeDraft>>(
      sessionKey(config.offlineEventId, "drafts"),
      {}
    )
  );
  const [submittingItem, setSubmittingItem] = useState<number | null>(null);
  const [submittingReplyId, setSubmittingReplyId] = useState<number | null>(
    null
  );
  const [modNoticeItems, setModNoticeItems] = useState<number[]>([]);
  const [canPostAsAdmin, setCanPostAsAdmin] = useState(false);
  const [postAsAdmin, setPostAsAdmin] = useState(false);
  const [attachmentsByItem, setAttachmentsByItem] = useState<
    Record<number, File[]>
  >({});
  const [attachErrorByItem, setAttachErrorByItem] = useState<
    Record<number, string>
  >({});
  const [keepPrivateByItem, setKeepPrivateByItem] = useState<
    Record<number, boolean>
  >({});
  const [replyOpen, setReplyOpen] = useState<number[]>([]);
  const [replyDrafts, setReplyDrafts] = useState<Record<number, string>>({});
  const [replyAsAdmin, setReplyAsAdminState] = useState<
    Record<number, boolean>
  >({});
  // Comment ids the current session reported (flagged) — a private confirmation, not public state.
  const [reportedIds, setReportedIds] = useState<Set<number>>(new Set());

  const named = config.modes.identity === "named";

  // Identify the name field so we can route validation focus to it.
  const nameFieldId = useMemo(() => {
    const f =
      config.fields.find(
        (x) => /name/i.test(x.name) && !/last|email|phone|user/i.test(x.name)
      ) || config.fields.find((x) => /name/i.test(x.name));
    return f ? f.id : null;
  }, [config.fields]);

  const commenterName = useMemo(() => {
    const first = formData["First Name"] || formData["Name"] || "";
    const last = formData["Last Name"] || "";
    return (last ? `${first} ${last}` : first).trim();
  }, [formData]);

  const fetchComments = useCallback(async () => {
    try {
      const res = await fetch(
        `/Comments/GetAgendaItemComments?offlineEventId=${config.offlineEventId}`,
        { headers: { "X-Requested-With": "XMLHttpRequest" } }
      );
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      const map: Record<number, PublicComment[]> = {};
      (data.items || []).forEach((it: any) => {
        map[it.agendaItemId] = it.comments || [];
      });
      setCommentsByItem(map);
      // Only meeting admins may post on behalf of the agency.
      setCanPostAsAdmin(!!data.currentUser?.isAdmin);
    } catch (err) {
      // Leave whatever we have; the view degrades to "no comments yet".
      // eslint-disable-next-line no-console
      console.error("Failed to load agenda item comments", err);
    } finally {
      setLoading(false);
    }
  }, [config.offlineEventId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Persist the resident's entries for this session as they type (see "session memory" above).
  useEffect(() => {
    saveSession(sessionKey(config.offlineEventId, "fields"), formData);
  }, [formData, config.offlineEventId]);
  useEffect(() => {
    saveSession(sessionKey(config.offlineEventId, "drafts"), composeByItem);
  }, [composeByItem, config.offlineEventId]);

  const handleFieldChange = useCallback((fieldName: string, value: string) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const toggleItem = useCallback((id: number) => {
    setOpenItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const sortFor = useCallback(
    (itemId: number): SortMode => sortByItem[itemId] || "top",
    [sortByItem]
  );
  const setSort = useCallback((itemId: number, mode: SortMode) => {
    setSortByItem((prev) => ({ ...prev, [itemId]: mode }));
  }, []);

  const composeFor = useCallback(
    (itemId: number): ComposeDraft =>
      composeByItem[itemId] || { text: "", position: null },
    [composeByItem]
  );
  const setComposeText = useCallback((itemId: number, text: string) => {
    setComposeByItem((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || { position: null }), text }
    }));
  }, []);
  const setComposePosition = useCallback(
    (itemId: number, pos: CommentPosition) => {
      setComposeByItem((prev) => ({
        ...prev,
        [itemId]: { ...(prev[itemId] || { text: "" }), position: pos }
      }));
    },
    []
  );

  // ---- attachments (per item) ----------------------------------------------
  const attachmentsFor = useCallback(
    (itemId: number): File[] => attachmentsByItem[itemId] || [],
    [attachmentsByItem]
  );
  const attachmentErrorFor = useCallback(
    (itemId: number) => attachErrorByItem[itemId],
    [attachErrorByItem]
  );
  const addAttachments = useCallback(
    (itemId: number, files: FileList | File[]) => {
      const incoming = Array.from(files);
      if (incoming.length === 0) return;
      const existing = attachmentsByItem[itemId] || [];
      const check = validateFiles(incoming, existing.length, config);
      if (!check.ok) {
        setAttachErrorByItem((prev) => ({
          ...prev,
          [itemId]: check.error || "Invalid file."
        }));
        return;
      }
      setAttachErrorByItem((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      setAttachmentsByItem((prev) => ({
        ...prev,
        [itemId]: [...(prev[itemId] || []), ...incoming]
      }));
    },
    [attachmentsByItem, config]
  );
  const removeAttachment = useCallback((itemId: number, index: number) => {
    setAttachmentsByItem((prev) => ({
      ...prev,
      [itemId]: (prev[itemId] || []).filter((_, i) => i !== index)
    }));
    setAttachErrorByItem((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }, []);

  // ---- private (hidden) comment toggle (per item) --------------------------
  const keepPrivateFor = useCallback(
    (itemId: number): boolean => !!keepPrivateByItem[itemId],
    [keepPrivateByItem]
  );
  const setKeepPrivate = useCallback((itemId: number, value: boolean) => {
    setKeepPrivateByItem((prev) => ({ ...prev, [itemId]: value }));
  }, []);

  // ---- per-reply admin "post on behalf of the agency" toggle ----------------
  const replyAsAdminFor = useCallback(
    (commentId: number): boolean => !!replyAsAdmin[commentId],
    [replyAsAdmin]
  );
  const setReplyAsAdmin = useCallback((commentId: number, value: boolean) => {
    setReplyAsAdminState((prev) => ({ ...prev, [commentId]: value }));
  }, []);

  // ---- validation -----------------------------------------------------------
  const focusField = (fieldId: number | null) => {
    if (fieldId == null) return;
    // Shared with the markup that renders the inputs: a bare `pc-field-<id>` lookup returns
    // the FIRST match in the document, which on an events list showing two meetings is the
    // wrong meeting's input -- validation would scroll the resident away from their error.
    const el = document.getElementById(`${fieldIdPrefix(config)}field-${fieldId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      (el as HTMLElement).focus();
    }
  };

  // Required personal-info fields + named-mode name requirement. Returns true when valid;
  // otherwise sets errors and focuses the offending field so the user is routed to it.
  const validatePersonalInfo = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    let firstInvalidId: number | null = null;

    config.fields.forEach((field) => {
      if (field.required && !formData[field.name]?.trim()) {
        newErrors[field.name] = `${field.name} is required`;
        if (firstInvalidId == null) firstInvalidId = field.id;
      }
    });

    // Named mode: a name is required to attribute the comment on the public record.
    if (named && !commenterName) {
      const nameField = config.fields.find((f) => f.id === nameFieldId);
      const key = nameField ? nameField.name : "Name";
      if (!newErrors[key]) {
        newErrors[key] =
          "Enter your name to comment, or switch to anonymous mode.";
      }
      if (firstInvalidId == null) firstInvalidId = nameFieldId;
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      focusField(firstInvalidId);
      return false;
    }
    return true;
  }, [config.fields, formData, named, commenterName, nameFieldId]);

  const buildIdentityFields = useCallback(() => {
    // Always persist the resident's entered name so it's on the official record and visible to
    // admins. Public masking happens server-side in GetAgendaItemComments (DisplayName returns
    // "Anonymous" when AreUsernamesPublic is off) — don't drop the name at the data layer here.
    const displayName = commenterName;
    const email =
      formData["Email"] || formData["Email Address"] || formData["email"] || "";
    const phone = formData["Phone"] || formData["Phone Number"] || "";
    return { displayName, email, phone };
  }, [commenterName, formData]);

  const appendCustomAttrs = useCallback(
    (data: Record<string, string | number | boolean>) => {
      config.fields.forEach((field) => {
        const value = formData[field.name];
        if (value !== undefined && value !== "") {
          data[`user_custom_attr_${field.id}`] = value;
        }
      });
    },
    [config.fields, formData]
  );

  // ---- submit a comment on one item ----------------------------------------
  const submitComment = useCallback(
    async (itemId: number) => {
      const draft = composeByItem[itemId];
      const text = (draft?.text || "").trim();
      setModNoticeItems((prev) => prev.filter((i) => i !== itemId));
      if (!text) return;
      if (!validatePersonalInfo()) return;

      setSubmittingItem(itemId);
      try {
        const { displayName, email, phone } = buildIdentityFields();
        const disposition: string = config.modes.positionTags
          ? draft?.position === "favor"
            ? "for"
            : draft?.position === "oppose"
            ? "against"
            : ""
          : "";

        // When the meeting allows it and the resident opted in, submit privately (hidden).
        // Server maps !PostPublicly → HiddenByUser (visible only to the author and staff).
        const keepPrivate =
          config.allowPrivateComments && !!keepPrivateByItem[itemId];

        const postData: Record<string, string | number | boolean> = {
          key: "event",
          assetId: config.offlineEventId,
          offlineEventId: config.offlineEventId,
          projectId: config.projectId,
          agendaItemId: itemId,
          comment: text,
          disposition,
          displayName,
          email,
          phone,
          custId: config.customerId,
          CommentType: 0,
          PostPublicly: !keepPrivate,
          // Server re-verifies admin access (VerifyIsAdminValue) before honoring this.
          postAsAdmin: canPostAsAdmin && postAsAdmin
        };
        appendCustomAttrs(postData);

        // Attachments ride along as Base64 in commentAttachmentsJSON; the server validates
        // type/size against the meeting config and stores them (images → Cloudinary, docs → ImportFile).
        const files = attachmentsByItem[itemId] || [];
        if (config.allowAttachments && files.length > 0) {
          postData.commentAttachmentsJSON = JSON.stringify(
            await buildCommentAttachments(files)
          );
        }

        const res = await postForm("/Comments/Comment", postData, config);
        if (!res.ok) throw new Error(`status ${res.status}`);

        let held = false;
        try {
          const json = await res.json();
          const c = json?.comment;
          if (c) {
            const hidden = c.hidden ?? c.Hidden;
            const profane = c.containsProfanity ?? c.ContainsProfanity;
            held = !!hidden && !!profane;
          }
        } catch {
          /* response wasn't JSON we can read; rely on refetch */
        }

        // Clear the draft + attachments + private flag, keep the row open, and refresh from the
        // server (authoritative — includes the author-only pending card when civility held it).
        setComposeByItem((prev) => ({
          ...prev,
          [itemId]: { text: "", position: null }
        }));
        setAttachmentsByItem((prev) => {
          const next = { ...prev };
          delete next[itemId];
          return next;
        });
        setKeepPrivateByItem((prev) => {
          const next = { ...prev };
          delete next[itemId];
          return next;
        });
        setOpenItems((prev) =>
          prev.includes(itemId) ? prev : [...prev, itemId]
        );
        await fetchComments();
        if (held) {
          setModNoticeItems((prev) =>
            prev.includes(itemId) ? prev : [...prev, itemId]
          );
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to submit comment", err);
        setErrors((prev) => ({
          ...prev,
          _submit: "An error occurred while submitting. Please try again."
        }));
      } finally {
        setSubmittingItem(null);
      }
    },
    [
      composeByItem,
      attachmentsByItem,
      keepPrivateByItem,
      validatePersonalInfo,
      buildIdentityFields,
      appendCustomAttrs,
      config,
      fetchComments,
      canPostAsAdmin,
      postAsAdmin
    ]
  );

  // ---- upvote ---------------------------------------------------------------
  const toggleVote = useCallback(
    async (itemId: number, commentId: number) => {
      let nowVoted = false;
      setCommentsByItem((prev) => {
        const list = prev[itemId] || [];
        const next = list.map((c) => {
          if (c.id !== commentId) return c;
          nowVoted = !c.votedByMe;
          return {
            ...c,
            votedByMe: nowVoted,
            votes: c.votes + (nowVoted ? 1 : -1)
          };
        });
        return { ...prev, [itemId]: next };
      });
      try {
        const url = nowVoted ? "/Comments/upVote" : "/Comments/unVote";
        const res = await postForm(
          url,
          { commentId, assetId: config.offlineEventId, key: "event" },
          config
        );
        if (!res.ok) throw new Error(`status ${res.status}`);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Vote failed; reconciling", err);
        await fetchComments();
      }
    },
    [config, fetchComments]
  );

  // ---- flag (report to admins; no public state change) ----------------------
  const flagComment = useCallback(
    async (commentId: number) => {
      const ok = window.confirm(
        "Report this comment to the moderators? They'll be notified to review it. The comment stays visible."
      );
      if (!ok) return;
      // Flagging only notifies admins (and is managed from the Project Admin dashboard).
      // The comment's display does not change for anyone; the reporter gets a private
      // "Reported" confirmation (this session only).
      setReportedIds((prev) => {
        const next = new Set(prev);
        next.add(commentId);
        return next;
      });
      try {
        const res = await postForm(
          "/Comments/FlagCommentNotifyAdmins",
          { commentId, issueType: "incivility", userText: "" },
          config
        );
        if (!res.ok) throw new Error(`status ${res.status}`);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Flag failed", err);
        setReportedIds((prev) => {
          const next = new Set(prev);
          next.delete(commentId);
          return next;
        });
      }
    },
    [config]
  );
  const reportedFor = useCallback(
    (commentId: number) => reportedIds.has(commentId),
    [reportedIds]
  );

  // ---- replies --------------------------------------------------------------
  const replyOpenFor = useCallback(
    (commentId: number) => replyOpen.includes(commentId),
    [replyOpen]
  );
  const openReply = useCallback((commentId: number) => {
    setReplyOpen((prev) =>
      prev.includes(commentId) ? prev : [...prev, commentId]
    );
  }, []);
  const cancelReply = useCallback((commentId: number) => {
    setReplyOpen((prev) => prev.filter((i) => i !== commentId));
  }, []);
  const replyDraftFor = useCallback(
    (commentId: number) => replyDrafts[commentId] || "",
    [replyDrafts]
  );
  const setReplyDraft = useCallback((commentId: number, text: string) => {
    setReplyDrafts((prev) => ({ ...prev, [commentId]: text }));
  }, []);

  const sendReply = useCallback(
    async (itemId: number, parentId: number) => {
      const text = (replyDrafts[parentId] || "").trim();
      if (!text) return;
      if (!validatePersonalInfo()) return;
      setSubmittingReplyId(parentId);
      try {
        const { displayName, email, phone } = buildIdentityFields();
        const postData: Record<string, string | number | boolean> = {
          key: "event",
          assetId: config.offlineEventId,
          offlineEventId: config.offlineEventId,
          projectId: config.projectId,
          agendaItemId: itemId,
          parentId,
          comment: text,
          displayName,
          email,
          phone,
          custId: config.customerId,
          CommentType: 0,
          PostPublicly: true,
          // Per-reply admin toggle — official replies post on behalf of the agency.
          // Server re-verifies admin access (VerifyIsAdminValue) before honoring this.
          postAsAdmin: canPostAsAdmin && !!replyAsAdmin[parentId]
        };
        appendCustomAttrs(postData);
        const res = await postForm("/Comments/Comment", postData, config);
        if (!res.ok) throw new Error(`status ${res.status}`);
        setReplyDrafts((prev) => ({ ...prev, [parentId]: "" }));
        setReplyOpen((prev) => prev.filter((i) => i !== parentId));
        await fetchComments();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to submit reply", err);
      } finally {
        setSubmittingReplyId(null);
      }
    },
    [
      replyDrafts,
      replyAsAdmin,
      validatePersonalInfo,
      buildIdentityFields,
      appendCustomAttrs,
      config,
      fetchComments,
      canPostAsAdmin
    ]
  );

  return {
    formData,
    errors,
    handleFieldChange,
    commenterName,
    loading,
    commentsByItem,
    openItems,
    toggleItem,
    searchKeyword,
    setSearchKeyword,
    sortFor,
    setSort,
    composeFor,
    setComposeText,
    setComposePosition,
    submittingItem,
    modNoticeItems,
    canPostAsAdmin,
    postAsAdmin,
    setPostAsAdmin,
    orgLabel: config.staffBadgeLabel,
    attachmentsFor,
    addAttachments,
    removeAttachment,
    attachmentErrorFor,
    keepPrivateFor,
    setKeepPrivate,
    submitComment,
    toggleVote,
    flagComment,
    reportedFor,
    replyOpenFor,
    openReply,
    cancelReply,
    replyDraftFor,
    setReplyDraft,
    replyAsAdminFor,
    setReplyAsAdmin,
    submittingReplyId,
    sendReply
  };
}
